import {
  supabaseClient,
} from "../../services/supabase";


export type TipoMovimento =
  | "entrada"
  | "saida";


export type FormaPagamentoFinanceiro =
  | "pix"
  | "dinheiro"
  | "debito"
  | "credito";


export type MovimentoCaixa = {
  id: string;

  tipo: TipoMovimento;

  descricao: string;

  valor: number;

  forma_pagamento:
    FormaPagamentoFinanceiro | null;

  origem: string | null;

  origem_id: string | null;

  data: string;

  created_at: string;
};


export type ResumoFinanceiro = {
  entradas: number;

  saidas: number;

  saldo: number;

  pix: number;

  dinheiro: number;

  debito: number;

  credito: number;
};

export type ResumoPorOrigem = {
  comandas: number;

  quadras: number;

  pacotes: number;

  aulasAvulsas: number;

  entradasManuais: number;

  despesasManuais: number;
};

export type NovoMovimentoManualInput = {
  tipo: TipoMovimento;

  descricao: string;

  valor: number;

  formaPagamento:
    FormaPagamentoFinanceiro | null;

  data: string;
};

export type FechamentoCaixa = {
  id: string;

  data: string;

  entradas_pix: number;
  entradas_dinheiro: number;
  entradas_debito: number;
  entradas_credito: number;

  saidas_pix: number;
  saidas_dinheiro: number;
  saidas_debito: number;
  saidas_credito: number;

  liquido_pix: number;
  liquido_dinheiro: number;
  liquido_debito: number;
  liquido_credito: number;

  entradas_total: number;
  saidas_total: number;
  liquido_total: number;

  esperado_pix: number;
  esperado_dinheiro: number;
  esperado_debito: number;
  esperado_credito: number;

  conferido_pix: number;
  conferido_dinheiro: number;
  conferido_debito: number;
  conferido_credito: number;

  diferenca_pix: number;
  diferenca_dinheiro: number;
  diferenca_debito: number;
  diferenca_credito: number;

  esperado_total: number;
  conferido_total: number;
  diferenca_total: number;

  observacao: string | null;

  fechado_por: string | null;

  status: "fechado" | "reaberto";

reaberto_em: string | null;

reaberto_por: string | null;

motivo_reabertura: string | null;

  fechado_em: string;
};


export type FecharCaixaInput = {
  data: string;

  conferidoPix: number;

  conferidoDinheiro: number;

  conferidoDebito: number;

  conferidoCredito: number;

  observacao: string;
};


export async function loadMovimentosPeriodo(
  dataInicio: string,
  dataFim: string,
): Promise<MovimentoCaixa[]> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from(
        "movimentos_caixa",
      )
      .select(
        `
          id,
          tipo,
          descricao,
          valor,
          forma_pagamento,
          origem,
          origem_id,
          data,
          created_at
        `,
      )
      .gte(
        "data",
        dataInicio,
      )
      .lte(
        "data",
        dataFim,
      )
      .order(
        "data",
        {
          ascending: false,
        },
      )
      .order(
        "created_at",
        {
          ascending: false,
        },
      );


  if (error) {
    throw error;
  }


  return (
    data ??
    []
  ).map(
    (item) => ({
      ...item,

      valor:
        Number(
          item.valor,
        ),
    }),
  ) as MovimentoCaixa[];
}


export function calcularResumoFinanceiro(
  movimentos:
    MovimentoCaixa[],
): ResumoFinanceiro {
  let entradas = 0;
  let saidas = 0;

  let pix = 0;
  let dinheiro = 0;
  let debito = 0;
  let credito = 0;


  movimentos.forEach(
    (movimento) => {
      const valor =
        Number(
          movimento.valor,
        );


      if (
        movimento.tipo ===
        "entrada"
      ) {
        entradas +=
          valor;
      } else {
        saidas +=
          valor;
      }


      if (
        movimento.forma_pagamento ===
        "pix"
      ) {
        pix +=
          movimento.tipo ===
            "entrada"
            ? valor
            : -valor;
      }


      if (
        movimento.forma_pagamento ===
        "dinheiro"
      ) {
        dinheiro +=
          movimento.tipo ===
            "entrada"
            ? valor
            : -valor;
      }


      if (
        movimento.forma_pagamento ===
        "debito"
      ) {
        debito +=
          movimento.tipo ===
            "entrada"
            ? valor
            : -valor;
      }


      if (
        movimento.forma_pagamento ===
        "credito"
      ) {
        credito +=
          movimento.tipo ===
            "entrada"
            ? valor
            : -valor;
      }
    },
  );


  return {
    entradas,

    saidas,

    saldo:
      entradas -
      saidas,

    pix,

    dinheiro,

    debito,

    credito,
  };
}

export function calcularResumoPorOrigem(
  movimentos:
    MovimentoCaixa[],
): ResumoPorOrigem {
  const resumo:
    ResumoPorOrigem = {
      comandas: 0,

      quadras: 0,

      pacotes: 0,

      aulasAvulsas: 0,

      entradasManuais: 0,

      despesasManuais: 0,
    };


  movimentos.forEach(
    (movimento) => {
      const valor =
        Number(
          movimento.valor,
        );


      switch (
        movimento.origem
      ) {
        case "comanda":
          resumo.comandas +=
            valor;
          break;

        case "quadra":
          resumo.quadras +=
            valor;
          break;

        case "aula_pacote":
          resumo.pacotes +=
            valor;
          break;

        case "aula_avulsa":
          resumo.aulasAvulsas +=
            valor;
          break;

        case "entrada_manual":
          resumo.entradasManuais +=
            valor;
          break;

        case "despesa_manual":
          resumo.despesasManuais +=
            valor;
          break;
      }
    },
  );


  return resumo;
}


export async function criarMovimentoManual(
  input:
    NovoMovimentoManualInput,
): Promise<MovimentoCaixa> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "criar_movimento_manual",
      {
        p_tipo:
          input.tipo,

        p_descricao:
          input.descricao,

        p_valor:
          input.valor,

        p_forma_pagamento:
          input.formaPagamento,

        p_data:
          input.data,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "O lançamento não retornou dados.",
    );
  }


  return {
    ...data,

    valor:
      Number(
        data.valor,
      ),
  } as MovimentoCaixa;
}

export async function fecharCaixaDia(
  input: FecharCaixaInput,
): Promise<FechamentoCaixa> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "fechar_caixa_dia",
      {
        p_data:
          input.data,

        p_conferido_pix:
          input.conferidoPix,

        p_conferido_dinheiro:
          input.conferidoDinheiro,

        p_conferido_debito:
          input.conferidoDebito,

        p_conferido_credito:
          input.conferidoCredito,

        p_observacao:
          input.observacao.trim() ||
          null,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "O fechamento não retornou dados.",
    );
  }


  return {
    ...data,

    entradas_pix:
      Number(
        data.entradas_pix,
      ),

    entradas_dinheiro:
      Number(
        data.entradas_dinheiro,
      ),

    entradas_debito:
      Number(
        data.entradas_debito,
      ),

    entradas_credito:
      Number(
        data.entradas_credito,
      ),

    saidas_pix:
      Number(
        data.saidas_pix,
      ),

    saidas_dinheiro:
      Number(
        data.saidas_dinheiro,
      ),

    saidas_debito:
      Number(
        data.saidas_debito,
      ),

    saidas_credito:
      Number(
        data.saidas_credito,
      ),

    liquido_pix:
      Number(
        data.liquido_pix,
      ),

    liquido_dinheiro:
      Number(
        data.liquido_dinheiro,
      ),

    liquido_debito:
      Number(
        data.liquido_debito,
      ),

    liquido_credito:
      Number(
        data.liquido_credito,
      ),

    entradas_total:
      Number(
        data.entradas_total,
      ),

    saidas_total:
      Number(
        data.saidas_total,
      ),

    liquido_total:
      Number(
        data.liquido_total,
      ),

    esperado_pix:
      Number(
        data.esperado_pix,
      ),

    esperado_dinheiro:
      Number(
        data.esperado_dinheiro,
      ),

    esperado_debito:
      Number(
        data.esperado_debito,
      ),

    esperado_credito:
      Number(
        data.esperado_credito,
      ),

    conferido_pix:
      Number(
        data.conferido_pix,
      ),

    conferido_dinheiro:
      Number(
        data.conferido_dinheiro,
      ),

    conferido_debito:
      Number(
        data.conferido_debito,
      ),

    conferido_credito:
      Number(
        data.conferido_credito,
      ),

    diferenca_pix:
      Number(
        data.diferenca_pix,
      ),

    diferenca_dinheiro:
      Number(
        data.diferenca_dinheiro,
      ),

    diferenca_debito:
      Number(
        data.diferenca_debito,
      ),

    diferenca_credito:
      Number(
        data.diferenca_credito,
      ),

    esperado_total:
      Number(
        data.esperado_total,
      ),

    conferido_total:
      Number(
        data.conferido_total,
      ),

    diferenca_total:
      Number(
        data.diferenca_total,
      ),
  } as FechamentoCaixa;
}

export async function loadFechamentosPeriodo(
  dataInicio: string,
  dataFim: string,
): Promise<FechamentoCaixa[]> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from(
        "fechamentos_caixa",
      )
      .select(
        `
          id,
          data,

          entradas_pix,
          entradas_dinheiro,
          entradas_debito,
          entradas_credito,

          saidas_pix,
          saidas_dinheiro,
          saidas_debito,
          saidas_credito,

          liquido_pix,
          liquido_dinheiro,
          liquido_debito,
          liquido_credito,

          entradas_total,
          saidas_total,
          liquido_total,

          esperado_pix,
          esperado_dinheiro,
          esperado_debito,
          esperado_credito,

          conferido_pix,
          conferido_dinheiro,
          conferido_debito,
          conferido_credito,

          diferenca_pix,
          diferenca_dinheiro,
          diferenca_debito,
          diferenca_credito,

          esperado_total,
          conferido_total,
          diferenca_total,

          observacao,
fechado_por,
status,
reaberto_em,
reaberto_por,
motivo_reabertura,
fechado_em
        `,
      )
      .gte(
        "data",
        dataInicio,
      )
      .lte(
        "data",
        dataFim,
      )
      .order(
        "data",
        {
          ascending:
            false,
        },
      );


  if (error) {
    throw error;
  }


  return (
    data ??
    []
  ).map(
    (item) => ({
      ...item,

      entradas_pix:
        Number(
          item.entradas_pix,
        ),

      entradas_dinheiro:
        Number(
          item.entradas_dinheiro,
        ),

      entradas_debito:
        Number(
          item.entradas_debito,
        ),

      entradas_credito:
        Number(
          item.entradas_credito,
        ),

      saidas_pix:
        Number(
          item.saidas_pix,
        ),

      saidas_dinheiro:
        Number(
          item.saidas_dinheiro,
        ),

      saidas_debito:
        Number(
          item.saidas_debito,
        ),

      saidas_credito:
        Number(
          item.saidas_credito,
        ),

      liquido_pix:
        Number(
          item.liquido_pix,
        ),

      liquido_dinheiro:
        Number(
          item.liquido_dinheiro,
        ),

      liquido_debito:
        Number(
          item.liquido_debito,
        ),

      liquido_credito:
        Number(
          item.liquido_credito,
        ),

      entradas_total:
        Number(
          item.entradas_total,
        ),

      saidas_total:
        Number(
          item.saidas_total,
        ),

      liquido_total:
        Number(
          item.liquido_total,
        ),

      esperado_pix:
        Number(
          item.esperado_pix,
        ),

      esperado_dinheiro:
        Number(
          item.esperado_dinheiro,
        ),

      esperado_debito:
        Number(
          item.esperado_debito,
        ),

      esperado_credito:
        Number(
          item.esperado_credito,
        ),

      conferido_pix:
        Number(
          item.conferido_pix,
        ),

      conferido_dinheiro:
        Number(
          item.conferido_dinheiro,
        ),

      conferido_debito:
        Number(
          item.conferido_debito,
        ),

      conferido_credito:
        Number(
          item.conferido_credito,
        ),

      diferenca_pix:
        Number(
          item.diferenca_pix,
        ),

      diferenca_dinheiro:
        Number(
          item.diferenca_dinheiro,
        ),

      diferenca_debito:
        Number(
          item.diferenca_debito,
        ),

      diferenca_credito:
        Number(
          item.diferenca_credito,
        ),

      esperado_total:
        Number(
          item.esperado_total,
        ),

      conferido_total:
        Number(
          item.conferido_total,
        ),

      diferenca_total:
        Number(
          item.diferenca_total,
        ),
    }),
  ) as FechamentoCaixa[];
}

export type EditarMovimentoManualInput = {
  id: string;

  descricao: string;

  valor: number;

  formaPagamento:
    FormaPagamentoFinanceiro;
};


export async function editarMovimentoManual(
  input:
    EditarMovimentoManualInput,
): Promise<MovimentoCaixa> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "editar_movimento_manual",
      {
        p_id:
          input.id,

        p_descricao:
          input.descricao,

        p_valor:
          input.valor,

        p_forma_pagamento:
          input.formaPagamento,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "A edição não retornou dados.",
    );
  }


  return {
    ...data,

    valor:
      Number(
        data.valor,
      ),
  } as MovimentoCaixa;
}


export async function estornarMovimentoManual(
  movimentoId: string,
): Promise<MovimentoCaixa> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "estornar_movimento_manual",
      {
        p_id:
          movimentoId,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "O estorno não retornou dados.",
    );
  }


  return {
    ...data,

    valor:
      Number(
        data.valor,
      ),
  } as MovimentoCaixa;
}
export async function reabrirCaixaDia(
  data: string,
  motivo: string,
): Promise<FechamentoCaixa> {
  const {
    data: resultado,
    error,
  } =
    await supabaseClient.rpc(
      "reabrir_caixa_dia",
      {
        p_data:
          data,

        p_motivo:
          motivo,
      },
    );


  if (error) {
    throw error;
  }


  if (!resultado) {
    throw new Error(
      "A reabertura não retornou dados.",
    );
  }


  return {
    ...resultado,

    entradas_total:
      Number(
        resultado.entradas_total,
      ),

    saidas_total:
      Number(
        resultado.saidas_total,
      ),

    liquido_total:
      Number(
        resultado.liquido_total,
      ),

    esperado_total:
      Number(
        resultado.esperado_total,
      ),

    conferido_total:
      Number(
        resultado.conferido_total,
      ),

    diferenca_total:
      Number(
        resultado.diferenca_total,
      ),
  } as FechamentoCaixa;
}
